import type { ApiResponse } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { CommissionLedgerReadRepository } from './repository';
import type { CommissionLedgerListQuery } from './types';
import { bangkokWallTimeToRfc3339 } from './types';

type LedgerListResponse = ApiResponse<'/api/v1/commission-ledger', 'get'>;
type LedgerDetailResponse = ApiResponse<'/api/v1/commission-ledger/{id}', 'get'>;

export function createRealCommissionLedgerRepository(
  client: ApiClient,
  accessToken: string | null,
): CommissionLedgerReadRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const result = await client.get<LedgerListResponse>('/api/v1/commission-ledger', {
        ...auth,
        query: listQuery(query),
        signal,
      });
      return {
        items: result.data.data,
        nextCursor: result.data.pagination.nextCursor,
        requestId: result.requestId,
      };
    },
    async get(entryId, signal) {
      const result = await client.get<LedgerDetailResponse>(ledgerPath(entryId), {
        ...auth,
        signal,
      });
      return result.data.data;
    },
  };
}

function listQuery(query: CommissionLedgerListQuery): Readonly<Record<string, unknown>> {
  const filters = query.filters;
  return Object.fromEntries(
    Object.entries({
      cursor: query.cursor,
      limit: query.pageSize,
      sort: query.sort,
      partnerId: filters.partnerId.trim(),
      agentId: filters.agentId.trim(),
      beneficiaryType: filters.beneficiaryType,
      beneficiaryId: filters.beneficiaryId.trim(),
      beneficiaryUsername: filters.beneficiaryUsername.trim(),
      transactionId: filters.transactionId.trim(),
      partnerTransactionId: filters.partnerTransactionId.trim(),
      entryType: filters.entryType,
      currency: filters.currency.trim().toUpperCase(),
      minimumAmount: filters.minimumAmount.trim(),
      maximumAmount: filters.maximumAmount.trim(),
      effectiveFrom: bangkokWallTimeToRfc3339(filters.effectiveFrom),
      effectiveTo: bangkokWallTimeToRfc3339(filters.effectiveTo),
    }).filter(([, value]) => value !== ''),
  );
}

function ledgerPath(entryId: string): string {
  return `/api/v1/commission-ledger/${encodeURIComponent(entryId)}`;
}
